/* eslint-disable sonarjs/cognitive-complexity, sonarjs/no-nested-template-literals -- Phone number formatting requires complex conditional logic */
/**
 * Formats phone numbers based on country code with proper spacing and separators.
 *
 * Supports the following country codes:
 * - +44 (UK): 0123 456789
 * - +55 (Brazil): 12 12345-6789
 * - +34 (Spain): 123 456 789
 * - +45 (Denmark): 20 12 34 56
 * - +352 (Luxembourg): 62 12 34 56
 * - +49 (Germany): Variable area code + local number
 * - +385 (Croatia): 123 456 789
 * - +31 (Netherlands): 123 456 789
 * - +33 (France): 1 23 45 67 89
 * - +353 (Ireland): 87 123 4567
 * - +52 (Mexico): 55 1234 5678
 * - +39 (Italy): 339 123 4567
 * - +351 (Portugal): 91 234 5678
 * - +32 (Belgium): 472 599 778
 * - +30 (Greece): 694 567 8901
 * - +7 (Russia): (916) 123-45-67
 * - +380 (Ukraine): 50 123 4567
 * - +46 (Sweden): 70 123 4567
 * - +40 (Romania): 70 123 4567
 * - +358 (Finland): 40 123 4567
 * - +593 (Ecuador): 98 123 4567
 * - +966 (Saudi Arabia): 50 123 4567
 * - +971 (UAE): 50 123 4567
 * - +64 (New Zealand): 21 123 4567
 * - +41 (Switzerland): 79 123 45 67
 * - +43 (Austria): 660 12345
 * - +420 (Czech Republic): 601 234 567
 * - +48 (Poland): 501 234 567
 * - +421 (Slovakia): 905 123 456
 * - +51 (Peru): 987 654 321
 * - +595 (Paraguay): 981 123 456
 * - +61 (Australia): 412 345 678
 * - +36 (Hungary): 20 123 456
 * - +359 (Bulgaria): 87 123 456
 * - +386 (Slovenia): 31 234 567
 * - +387 (Bosnia-Herzegovina): 61 234 567
 * - +357 (Cyprus): 9612 3456
 * - +598 (Uruguay): 9123 4567
 * - +506 (Costa Rica): 8765 4321
 * - +974 (Qatar): 3345 6789
 * - +57 (Colombia): 310 123 4567
 * - +58 (Venezuela): 412 123 4567
 * - +90 (Turkey): 532 123 4567
 * - +381 (Serbia): 63 1234567
 * - +47 (Norway): 468 12 345
 * - +54 (Argentina): 1234567890 (no formatting due to variable area codes)
 * - +56 (Chile): 2 1234 5678
 * - +81 (Japan): 90 1234 5678
 * - +354 (Iceland): 777 1234
 * - +591 (Bolivia): 7 123 4567
 * - Default (USA): (123) 456-7890
 * @param input - The phone number string to format (can include non-digit characters)
 * @param includeCountryCode - Whether to include the country code prefix in the output
 * @param countryCode - The country code to determine formatting pattern (e.g., "+1", "+52", "+44", "+39", "+30", "+380", "+46", "+40", "+358", "+593", "+966", "+971", "+64", "+41", "+43", "+420", "+48", "+421", "+51", "+595", "+61", "+36", "+359", "+386", "+387", "+357", "+598", "+506", "+974", "+57", "+58", "+90", "+381", "+47", "+54", "+56", "+81", "+354", "+591")
 * @returns The formatted phone number string
 * @example
 * formatPhoneNumber("5512345678", false, "+52") // "55 1234 5678"
 * formatPhoneNumber("5512345678", true, "+52") // "+52 55 1234 5678"
 * formatPhoneNumber("3391234567", false, "+39") // "339 123 4567"
 * formatPhoneNumber("912345678", false, "+351") // "91 234 5678"
 * formatPhoneNumber("472599778", false, "+32") // "472 599 778"
 * formatPhoneNumber("6945678901", false, "+30") // "694 567 8901"
 * formatPhoneNumber("9161234567", false, "+7") // "(916) 123-45-67"
 * formatPhoneNumber("501234567", false, "+380") // "50 123 4567"
 * formatPhoneNumber("701234567", false, "+46") // "70 123 4567"
 * formatPhoneNumber("791234567", false, "+41") // "79 123 45 67"
 * formatPhoneNumber("66012345", false, "+43") // "660 12345"
 * formatPhoneNumber("1234567890", false, "+1") // "(123) 456-7890"
 */
export default function formatPhoneNumber(
  input: string,
  includeCountryCode?: boolean,
  countryCode?: string
): string {
  const cleaned = input.replace(/\D/g, "");

  // Apply formatting based on the country code
  switch (countryCode) {
    // UK format: 0123 456789
    case "+44": {
      const countryCodeBase =
        cleaned.length > 10 ? cleaned.substring(0, cleaned.length - 10) : "";
      const localNumber = cleaned.slice(-10);
      const areaCode = localNumber.substring(0, 4);
      const firstPart = localNumber.substring(4, 10);
      const formattedNumberUK = `${areaCode}${firstPart ? ` ${firstPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberUK}`
        : formattedNumberUK;
    }
    // Brazil format: 12 12345-6789
    case "+55": {
      const countryCodeBase =
        cleaned.length > 11 ? cleaned.substring(0, cleaned.length - 11) : "";
      const localNumber = cleaned.slice(-11);
      const areaCode = localNumber.substring(0, 2);
      const firstPart = localNumber.substring(2, 7);
      const secondPart = localNumber.substring(7, 11);
      const formattedNumberBrazil = `${areaCode}${firstPart ? ` ${firstPart}` : ""}${secondPart ? `-${secondPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberBrazil}`
        : formattedNumberBrazil;
    }
    // Spain format: 123 456 789
    case "+34": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const areaCode = localNumber.substring(0, 3);
      const firstPart = localNumber.substring(3, 6);
      const secondPart = localNumber.substring(6, 9);
      const formattedNumberSpain = `${areaCode}${firstPart ? ` ${firstPart}` : ""}${secondPart ? ` ${secondPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberSpain}`
        : formattedNumberSpain;
    }
    // Belgium format: XXX XXX XXX (9 digits)
    case "+32": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const firstPart = localNumber.substring(0, 3);
      const secondPart = localNumber.substring(3, 6);
      const thirdPart = localNumber.substring(6, 9);
      const formattedNumberBelgium = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberBelgium}`
        : formattedNumberBelgium;
    }
    // Group F countries: XX XX XX XX pattern (8 digits)
    case "+352": // Luxembourg
    case "+45": {
      // Denmark
      const countryCodeBase =
        cleaned.length > 8 ? cleaned.substring(0, cleaned.length - 8) : "";
      const localNumber = cleaned.slice(-8);
      const firstPart = localNumber.substring(0, 2);
      const secondPart = localNumber.substring(2, 4);
      const thirdPart = localNumber.substring(4, 6);
      const fourthPart = localNumber.substring(6, 8);
      const formattedNumberGroupF = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}${fourthPart ? ` ${fourthPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberGroupF}`
        : formattedNumberGroupF;
    }
    // Iceland format: 777 1234
    case "+354": {
      const countryCodeBase =
        cleaned.length > 7 ? cleaned.substring(0, cleaned.length - 7) : "";
      const localNumber = cleaned.slice(-7);
      const firstPart = localNumber.substring(0, 3);
      const secondPart = localNumber.substring(3, 7);
      const formattedNumberIceland = `${firstPart}${secondPart ? ` ${secondPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberIceland}`
        : formattedNumberIceland;
    }
    // Germany format: Variable area code + local number
    case "+49": {
      const countryCodeBase =
        cleaned.length > 11 ? cleaned.substring(0, cleaned.length - 11) : "";
      const localNumber = cleaned.slice(-11);

      // Determine area code length based on first digit
      const areaCodeLength = localNumber.startsWith("1")
        ? 4
        : localNumber.startsWith("2") || localNumber.startsWith("3")
          ? Math.min(5, localNumber.length - 4)
          : Math.min(4, localNumber.length - 5);

      const areaCode = localNumber.substring(0, areaCodeLength);
      const remainingDigits = localNumber.substring(areaCodeLength);

      const formattedNumberGermany = `${areaCode}${remainingDigits}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberGermany}`
        : formattedNumberGermany;
    }
    // Croatia format: 123 456 789
    case "+385": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const areaCode = localNumber.substring(0, 3);
      const firstPart = localNumber.substring(3, 6);
      const secondPart = localNumber.substring(6, 9);
      const formattedNumberCroatia = `${areaCode}${firstPart ? ` ${firstPart}` : ""}${secondPart ? ` ${secondPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberCroatia}`
        : formattedNumberCroatia;
    }
    // The Netherlands format: 123 456 789 (9 digits)
    case "+31": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const areaCode = localNumber.substring(0, 3);
      const firstPart = localNumber.substring(3, 6);
      const secondPart = localNumber.substring(6, 9);
      const formattedNumberNetherlands = `${areaCode}${firstPart ? ` ${firstPart}` : ""}${secondPart ? ` ${secondPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberNetherlands}`
        : formattedNumberNetherlands;
    }
    // France format: 1 23 45 67 89 (9 digits)
    case "+33": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const firstDigit = localNumber.substring(0, 1);
      const firstPart = localNumber.substring(1, 3);
      const secondPart = localNumber.substring(3, 5);
      const thirdPart = localNumber.substring(5, 7);
      const fourthPart = localNumber.substring(7, 9);
      const formattedNumberFrance = `${firstDigit} ${firstPart} ${secondPart} ${thirdPart} ${fourthPart}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberFrance}`
        : formattedNumberFrance;
    }
    // Ireland format: 87 123 4567
    case "+353": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const areaCode = localNumber.substring(0, 2);
      const firstPart = localNumber.substring(2, 5);
      const secondPart = localNumber.substring(5, 9);
      const formattedNumberIreland = `${areaCode} ${firstPart} ${secondPart}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberIreland}`
        : formattedNumberIreland;
    }
    // Mexico format: 55 1234 5678
    case "+52": {
      const countryCodeBase =
        cleaned.length > 10 ? cleaned.substring(0, cleaned.length - 10) : "";
      const localNumber = cleaned.slice(-10);
      const areaCode = localNumber.substring(0, 2);
      const firstPart = localNumber.substring(2, 6);
      const secondPart = localNumber.substring(6, 10);
      const formattedNumberMexico = `${areaCode}${firstPart ? ` ${firstPart}` : ""}${secondPart ? ` ${secondPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberMexico}`
        : formattedNumberMexico;
    }
    // Argentina - no formatting due to variable area code complexity (2-4 digits)
    // Returns digits only for 10-digit numbers
    case "+54": {
      const countryCodeBase =
        cleaned.length > 10 ? cleaned.substring(0, cleaned.length - 10) : "";
      const localNumber = cleaned.slice(-10);
      // Return digits without formatting due to regional variations
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${localNumber}`
        : localNumber;
    }
    // Chile format: 2 1234 5678
    case "+56": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const firstPart = localNumber.substring(0, 1);
      const secondPart = localNumber.substring(1, 5);
      const thirdPart = localNumber.substring(5, 9);
      const formattedNumberChile = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberChile}`
        : formattedNumberChile;
    }
    // Portugal format: 91 234 5678
    case "+351": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const firstPart = localNumber.substring(0, 2);
      const secondPart = localNumber.substring(2, 5);
      const thirdPart = localNumber.substring(5, 9);
      const formattedNumberPortugal = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberPortugal}`
        : formattedNumberPortugal;
    }
    // XXX XXX XXXX pattern: Italy, Greece, Group E countries (10 digits)
    case "+39": // Italy
    case "+30": // Greece
    // Group E countries
    // falls through
    case "+57": // Colombia
    case "+58": // Venezuela
    case "+90": {
      // Turkey
      const countryCodeBase =
        cleaned.length > 10 ? cleaned.substring(0, cleaned.length - 10) : "";
      const localNumber = cleaned.slice(-10);
      const firstPart = localNumber.substring(0, 3);
      const secondPart = localNumber.substring(3, 6);
      const thirdPart = localNumber.substring(6, 10);
      const formattedNumber = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumber}`
        : formattedNumber;
    }
    // Russia format: (916) 123-45-67
    case "+7": {
      const countryCodeBase =
        cleaned.length > 10 ? cleaned.substring(0, cleaned.length - 10) : "";
      const localNumber = cleaned.slice(-10);
      const firstPart = localNumber.substring(0, 3);
      const secondPart = localNumber.substring(3, 6);
      const thirdPart = localNumber.substring(6, 8);
      const fourthPart = localNumber.substring(8, 10);
      const formattedNumberRussia = `${firstPart ? `(${firstPart}` : ""}${secondPart ? `) ${secondPart}` : ""}${thirdPart ? `-${thirdPart}` : ""}${fourthPart ? `-${fourthPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberRussia}`
        : formattedNumberRussia;
    }
    // Japan format: 90 1234 5678
    case "+81": {
      const countryCodeBase =
        cleaned.length > 10 ? cleaned.substring(0, cleaned.length - 10) : "";
      const localNumber = cleaned.slice(-10);
      const firstPart = localNumber.substring(0, 2);
      const secondPart = localNumber.substring(2, 6);
      const thirdPart = localNumber.substring(6, 10);
      const formattedNumberJapan = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberJapan}`
        : formattedNumberJapan;
    }
    // Ukraine format: 50 123 4567
    case "+380":
    // Group A countries: XX XXX XXXX pattern (9 digits)
    // falls through
    case "+46": // Sweden
    case "+40": // Romania
    case "+358": // Finland
    case "+593": // Ecuador
    case "+966": // Saudi Arabia
    case "+971": // United Arab Emirates
    case "+64": {
      // New Zealand
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const firstPart = localNumber.substring(0, 2);
      const secondPart = localNumber.substring(2, 5);
      const thirdPart = localNumber.substring(5, 9);
      const formattedNumberUkraine = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberUkraine}`
        : formattedNumberUkraine;
    }
    // Switzerland format: 79 123 45 67
    case "+41": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const firstPart = localNumber.substring(0, 2);
      const secondPart = localNumber.substring(2, 5);
      const thirdPart = localNumber.substring(5, 7);
      const fourthPart = localNumber.substring(7, 9);
      const formattedNumberSwitzerland = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}${fourthPart ? ` ${fourthPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberSwitzerland}`
        : formattedNumberSwitzerland;
    }
    // Austria format: 660 12345
    case "+43": {
      const countryCodeBase =
        cleaned.length > 8 ? cleaned.substring(0, cleaned.length - 8) : "";
      const localNumber = cleaned.slice(-8);
      const firstPart = localNumber.substring(0, 3);
      const secondPart = localNumber.substring(3, 8);
      const formattedNumberAustria = `${firstPart}${secondPart ? ` ${secondPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberAustria}`
        : formattedNumberAustria;
    }
    // Czech Republic format: 601 234 567
    case "+420": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const firstPart = localNumber.substring(0, 3);
      const secondPart = localNumber.substring(3, 6);
      const thirdPart = localNumber.substring(6, 9);
      const formattedNumberCzechRepublic = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberCzechRepublic}`
        : formattedNumberCzechRepublic;
    }
    // Poland format: 501 234 567
    case "+48":
    // Group B countries: XXX XXX XXX pattern (9 digits)
    // falls through
    case "+421": // Slovakia
    case "+51": // Peru
    case "+595": // Paraguay
    case "+61": {
      // Australia
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const firstPart = localNumber.substring(0, 3);
      const secondPart = localNumber.substring(3, 6);
      const thirdPart = localNumber.substring(6, 9);
      const formattedNumberGroupB = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberGroupB}`
        : formattedNumberGroupB;
    }
    // Bolivia format: 7 123 4567
    case "+591": {
      const countryCodeBase =
        cleaned.length > 8 ? cleaned.substring(0, cleaned.length - 8) : "";
      const localNumber = cleaned.slice(-8);
      const firstPart = localNumber.substring(0, 1);
      const secondPart = localNumber.substring(1, 4);
      const thirdPart = localNumber.substring(4, 8);
      const formattedNumberBolivia = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberBolivia}`
        : formattedNumberBolivia;
    }
    // Group C countries: XX XXX XXX pattern (8 digits)
    case "+36": // Hungary
    case "+359": // Bulgaria
    case "+386": // Slovenia
    case "+387": {
      // Bosnia-Herzegovina
      const countryCodeBase =
        cleaned.length > 8 ? cleaned.substring(0, cleaned.length - 8) : "";
      const localNumber = cleaned.slice(-8);
      const firstPart = localNumber.substring(0, 2);
      const secondPart = localNumber.substring(2, 5);
      const thirdPart = localNumber.substring(5, 8);
      const formattedNumberGroupC = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberGroupC}`
        : formattedNumberGroupC;
    }
    // Group D countries: XXXX XXXX pattern (8 digits)
    case "+357": // Cyprus
    case "+598": // Uruguay
    case "+506": // Costa Rica
    case "+974": {
      // Qatar
      const countryCodeBase =
        cleaned.length > 8 ? cleaned.substring(0, cleaned.length - 8) : "";
      const localNumber = cleaned.slice(-8);
      const firstPart = localNumber.substring(0, 4);
      const secondPart = localNumber.substring(4, 8);
      const formattedNumberGroupD = `${firstPart}${secondPart ? ` ${secondPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberGroupD}`
        : formattedNumberGroupD;
    }
    // Serbia format: 63 1234567
    case "+381": {
      const countryCodeBase =
        cleaned.length > 9 ? cleaned.substring(0, cleaned.length - 9) : "";
      const localNumber = cleaned.slice(-9);
      const firstPart = localNumber.substring(0, 2);
      const secondPart = localNumber.substring(2, 9);
      const formattedNumberSerbia = `${firstPart}${secondPart ? ` ${secondPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberSerbia}`
        : formattedNumberSerbia;
    }
    // Norway format: 468 12 345
    case "+47": {
      const countryCodeBase =
        cleaned.length > 8 ? cleaned.substring(0, cleaned.length - 8) : "";
      const localNumber = cleaned.slice(-8);
      const firstPart = localNumber.substring(0, 3);
      const secondPart = localNumber.substring(3, 5);
      const thirdPart = localNumber.substring(5, 8);
      const formattedNumberNorway = `${firstPart}${secondPart ? ` ${secondPart}` : ""}${thirdPart ? ` ${thirdPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberNorway}`
        : formattedNumberNorway;
    }
    // USA format: (123) 456-7890
    default: {
      const countryCodeBase =
        cleaned.length > 10 ? cleaned.substring(0, cleaned.length - 10) : "";
      const localNumber = cleaned.slice(-10);
      const areaCode = localNumber.substring(0, 3);
      const firstPart = localNumber.substring(3, 6);
      const secondPart = localNumber.substring(6, 10);
      const formattedNumberUSA = `${areaCode ? `(${areaCode}` : ""}${firstPart ? `) ${firstPart}` : ""}${secondPart ? `-${secondPart}` : ""}`;
      return includeCountryCode && countryCodeBase
        ? `+${countryCodeBase} ${formattedNumberUSA}`
        : formattedNumberUSA;
    }
  }
}

/* eslint-enable sonarjs/cognitive-complexity, sonarjs/no-nested-template-literals -- Phone number formatting requires complex conditional logic */
